import React from 'react';
import { View } from '@adobe/react-spectrum';
import { EditorActions } from 'app/stores/editor/actions';
import { useAppDispatch } from 'app/stores/store';
import { SVG } from 'app/views/components/svg/SVG';
import { useConnect } from 'app/views/hooks/useConnect';
import { useHandleDrag } from 'app/views/hooks/useHandleDrag';
import { toPx } from 'app/utils/DOM';
import { fromUniform, toUniform } from 'app/utils/math';
const Handles = () => (React.createElement(React.Fragment, null,
    React.createElement("span", { className: "cropper-dashed dashed-h cropper-handle", "data-action": "drag" }),
    React.createElement("span", { className: "cropper-dashed dashed-v cropper-handle", "data-action": "drag" }),
    React.createElement("span", { className: "cropper-view-box cropper-handle", "data-action": "drag" }),
    React.createElement("span", { className: "cropper-center" }),
    React.createElement("span", { className: "cropper-line cropper-handle line-e", "data-action": "e" }),
    React.createElement("span", { className: "cropper-line cropper-handle line-n", "data-action": "n" }),
    React.createElement("span", { className: "cropper-line cropper-handle line-w", "data-action": "w" }),
    React.createElement("span", { className: "cropper-line cropper-handle line-s", "data-action": "s" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-e", "data-action": "e" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-n", "data-action": "n" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-w", "data-action": "w" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-s", "data-action": "s" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-ne", "data-action": "ne" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-nw", "data-action": "nw" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-sw", "data-action": "sw" }),
    React.createElement("span", { className: "cropper-point cropper-handle point-se", "data-action": "se" })));
export const Crop = () => {
    const dispatch = useAppDispatch();
    const initialRect = React.useRef();
    const containerRef = React.useRef(null);
    const { viewport, unifiedCropArea } = useConnect(state => {
        var _a;
        return ({
            viewport: state.viewport,
            unifiedCropArea: (_a = state.editor.controllerData) === null || _a === void 0 ? void 0 : _a.unifiedArea,
        });
    });
    const { design: designRect } = viewport;
    const getCropRect = React.useCallback(() => fromUniform(designRect, unifiedCropArea), [designRect, unifiedCropArea]);
    const setRect = React.useCallback((rect) => {
        const unifiedRect = toUniform(rect, designRect);
        dispatch(EditorActions.setControllerData({
            key: 'unifiedArea',
            value: unifiedRect,
        }));
    }, [designRect, dispatch]);
    const onDragStart = React.useCallback(() => {
        initialRect.current = getCropRect();
    }, [getCropRect]);
    const onHandleChanged = React.useCallback((handleId, point, downPoint) => {
        const irect = initialRect.current;
        const x = Math.max(0, Math.min(point.design.x, designRect.width));
        const y = Math.max(0, Math.min(point.design.y, designRect.height));
        const dx = x - downPoint.design.x;
        const dy = y - downPoint.design.y;
        let newRect;
        switch (handleId) {
            case 'drag':
                newRect = {
                    x: irect.x + dx,
                    y: irect.y + dy,
                    width: irect.width,
                    height: irect.height,
                };
                break;
            case 'nw':
                newRect = {
                    x: irect.x + dx,
                    y: irect.y + dy,
                    width: irect.width - dx,
                    height: irect.height - dy,
                };
                break;
            case 'ne':
                newRect = {
                    x: irect.x,
                    y: irect.y + dy,
                    width: irect.width + dx,
                    height: irect.height - dy,
                };
                break;
            case 'se':
                newRect = {
                    x: irect.x,
                    y: irect.y,
                    width: irect.width + dx,
                    height: irect.height + dy,
                };
                break;
            case 'sw':
                newRect = {
                    x: irect.x + dx,
                    y: irect.y,
                    width: irect.width - dx,
                    height: irect.height + dy,
                };
                break;
            case 'n':
                newRect = {
                    x: irect.x,
                    y: irect.y + dy,
                    width: irect.width,
                    height: irect.height - dy,
                };
                break;
            case 'w':
                newRect = {
                    x: irect.x + dx,
                    y: irect.y,
                    width: irect.width - dx,
                    height: irect.height,
                };
                break;
            case 's':
                newRect = {
                    x: irect.x,
                    y: irect.y,
                    width: irect.width,
                    height: irect.height + dy,
                };
                break;
            case 'e':
                newRect = {
                    x: irect.x,
                    y: irect.y,
                    width: irect.width + dx,
                    height: irect.height,
                };
                break;
            default:
                break;
        }
        if (newRect) {
            if (newRect.width < 10 || newRect.height < 10) {
                return;
            }
            setRect({
                x: Math.max(0, Math.min(newRect.x, designRect.width - newRect.width)),
                y: Math.max(0, Math.min(newRect.y, designRect.height - newRect.height)),
                width: newRect.width,
                height: newRect.height,
            });
        }
    }, [designRect, setRect]);
    const handlers = React.useMemo(() => ({
        onDragStart,
        onDrag: onHandleChanged,
        onDragEnd: onDragStart,
    }), [onDragStart, onHandleChanged]);
    useHandleDrag(containerRef, handlers, {
        nodeClass: 'cropper-handle',
        idProp: 'data-action',
        workspace: 'design',
    });
    const rect = getCropRect();
    if (!rect) {
        return null;
    }
    return (React.createElement(View, { position: "absolute", left: 0, top: 0, width: "100%", height: "100%" },
        React.createElement("div", { ref: containerRef, className: "crop-container", style: {
                left: toPx(designRect.x),
                top: toPx(designRect.y),
                width: toPx(designRect.width),
                height: toPx(designRect.height),
            } },
            React.createElement(SVG, { width: "100%", height: "100%", className: "cropper-canvas", viewBox: `0 0 ${designRect.width} ${designRect.height}` },
                React.createElement("defs", null,
                    React.createElement("mask", { id: "cropper-mask" },
                        React.createElement("rect", { width: "100%", height: "100%", fill: "white" }),
                        React.createElement("rect", { x: rect.x.toFixed(2), y: rect.y.toFixed(2), width: rect.width.toFixed(2), height: rect.height.toFixed(2), fill: "black" }))),
                React.createElement("rect", { mask: "url(#cropper-mask)", width: "100%", height: "100%", fill: "black", fillOpacity: 0.45 })),
            React.createElement("div", { style: {
                    position: 'absolute',
                    left: toPx(rect.x),
                    top: toPx(rect.y),
                    width: toPx(rect.width),
                    height: toPx(rect.height),
                } },
                React.createElement("div", { className: "cropper-crop-box dashed-border" },
                    React.createElement(Handles, null))))));
};
